import React, { useMemo, useState, Component } from 'react';
import { Search, Mail, PackageX, Send, Check, X, Signature, Store as StoreIcon, Clock, CheckCircle2, AlertCircle, Calendar, ChevronRight, ArrowLeft, MapPin, Package, Inbox, Truck } from 'lucide-react';
import { KpiCard } from '../KpiCard';
const CARRIER_LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png'
};
const CARRIER_EMAILS: Record<string, string> = {
  BRT: 'giacenze@brt.it',
  FedEx: 'support.italy@fedex.com',
  GLS: 'giacenze@gls-italy.com',
  SDA: 'giacenze@sda.it',
  UPS: 'support@ups.com'
};
type StockReason = 'Mancata firma' | 'Negozio chiuso';
type StockStatus = 'In giacenza' | 'Email inviata' | 'In riconsegna' | 'Risolto';
type EventKind = 'created' | 'email_sent' | 'email_reply' | 'redelivery' | 'resolved' | 'note';
type StockEvent = {
  id: string;
  kind: EventKind;
  timestamp: string;
  title: string;
  description?: string;
  author?: string;
};
type Stock = {
  id: string;
  shipmentRef: string;
  carrier: string;
  recipient: string;
  address: string;
  city: string;
  reason: StockReason;
  status: StockStatus;
  sinceDate: string;
  daysInStock: number;
  hubLocation: string;
  emailSentAt?: string;
  events: StockEvent[];
};
const INITIAL_STOCKS: Stock[] = [{
  id: 'GIA-501',
  shipmentRef: 'SP-71101',
  carrier: 'BRT',
  recipient: 'Boutique Roma Termini',
  address: 'Via Marsala 45',
  city: 'Roma',
  reason: 'Negozio chiuso',
  status: 'In giacenza',
  sinceDate: '23 mag 2026',
  daysInStock: 2,
  hubLocation: 'Hub BRT Roma Est',
  events: [{
    id: 'e1',
    kind: 'created',
    timestamp: '23 mag · 09:14',
    title: 'Giacenza aperta',
    description: 'Il corriere ha segnalato il negozio chiuso al primo passaggio.',
    author: 'Sistema'
  }]
}, {
  id: 'GIA-498',
  shipmentRef: 'SP-71067',
  carrier: 'GLS',
  recipient: 'Mario Bianchi',
  address: 'Via Torino 18',
  city: 'Milano',
  reason: 'Mancata firma',
  status: 'In giacenza',
  sinceDate: '23 mag 2026',
  daysInStock: 1,
  hubLocation: 'Hub GLS Milano Nord',
  events: [{
    id: 'e1',
    kind: 'created',
    timestamp: '23 mag · 11:42',
    title: 'Giacenza aperta',
    description: 'Destinatario assente al momento della consegna.',
    author: 'Sistema'
  }]
}, {
  id: 'GIA-495',
  shipmentRef: 'SP-71034',
  carrier: 'FedEx',
  recipient: 'Concept Store Napoli',
  address: 'Via Toledo 220',
  city: 'Napoli',
  reason: 'Negozio chiuso',
  status: 'Email inviata',
  sinceDate: '22 mag 2026',
  daysInStock: 3,
  hubLocation: 'Hub FedEx Napoli',
  emailSentAt: '22 mag · 14:32',
  events: [{
    id: 'e1',
    kind: 'created',
    timestamp: '22 mag · 10:05',
    title: 'Giacenza aperta',
    description: 'Negozio chiuso per inventario annuale.',
    author: 'Sistema'
  }, {
    id: 'e2',
    kind: 'email_sent',
    timestamp: '22 mag · 14:32',
    title: 'Email inviata a FedEx',
    description: 'Richiesta di riconsegna inviata a support.italy@fedex.com.',
    author: 'Magazzino Centrale'
  }]
}, {
  id: 'GIA-489',
  shipmentRef: 'SP-70991',
  carrier: 'UPS',
  recipient: 'Anna Verdi',
  address: 'Piazza Cavour 7',
  city: 'Torino',
  reason: 'Mancata firma',
  status: 'In riconsegna',
  sinceDate: '21 mag 2026',
  daysInStock: 4,
  hubLocation: 'Hub UPS Torino',
  emailSentAt: '21 mag · 16:10',
  events: [{
    id: 'e1',
    kind: 'created',
    timestamp: '21 mag · 09:28',
    title: 'Giacenza aperta',
    description: 'Destinatario non presente. Avviso lasciato in cassetta.',
    author: 'Sistema'
  }, {
    id: 'e2',
    kind: 'email_sent',
    timestamp: '21 mag · 16:10',
    title: 'Email inviata a UPS',
    description: 'Richiesta di riconsegna inviata a support@ups.com.',
    author: 'Magazzino Centrale'
  }, {
    id: 'e3',
    kind: 'email_reply',
    timestamp: '22 mag · 08:45',
    title: 'Risposta da UPS',
    description: 'Confermiamo la presa in carico. Riconsegna prevista per il 24/05/2026 in fascia 14:00-18:00. Numero ticket UPS: #87234561.',
    author: 'UPS Support'
  }, {
    id: 'e4',
    kind: 'redelivery',
    timestamp: '22 mag · 09:00',
    title: 'Riconsegna pianificata',
    description: 'La giacenza è passata in stato "In riconsegna".',
    author: 'Sistema'
  }]
}, {
  id: 'GIA-482',
  shipmentRef: 'SP-70912',
  carrier: 'SDA',
  recipient: 'Outlet Bologna',
  address: 'Via Indipendenza 88',
  city: 'Bologna',
  reason: 'Negozio chiuso',
  status: 'In giacenza',
  sinceDate: '20 mag 2026',
  daysInStock: 5,
  hubLocation: 'Hub SDA Bologna',
  events: [{
    id: 'e1',
    kind: 'created',
    timestamp: '20 mag · 16:18',
    title: 'Giacenza aperta',
    description: 'Negozio chiuso per riposo settimanale del lunedì.',
    author: 'Sistema'
  }]
}];
const REASON_CONFIG: Record<StockReason, {
  icon: ComponentType<any>;
  color: string;
}> = {
  'Mancata firma': {
    icon: Signature,
    color: 'text-[#F5A524]'
  },
  'Negozio chiuso': {
    icon: StoreIcon,
    color: 'text-[#5DA9FF]'
  }
};
const STATUS_CONFIG: Record<StockStatus, {
  bg: string;
  text: string;
  border: string;
  icon: ComponentType<any>;
}> = {
  'In giacenza': {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    icon: PackageX
  },
  'Email inviata': {
    bg: 'bg-[#5DA9FF]/10',
    text: 'text-[#5DA9FF]',
    border: 'border-[#5DA9FF]/30',
    icon: Mail
  },
  'In riconsegna': {
    bg: 'bg-[#D4FF3A]/10',
    text: 'text-[#D4FF3A]',
    border: 'border-[#D4FF3A]/30',
    icon: Clock
  },
  Risolto: {
    bg: 'bg-[#1F1F1F]',
    text: 'text-[#9A9A9A]',
    border: 'border-[#262626]',
    icon: CheckCircle2
  }
};
export function Giacenze() {
  const [stocks, setStocks] = useState<Stock[]>(INITIAL_STOCKS);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'Tutti' | StockReason>('Tutti');
  const [emailModal, setEmailModal] = useState<Stock | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const filtered = useMemo(() => stocks.filter(s => {
    const matchQ = !query.trim() || `${s.id} ${s.shipmentRef} ${s.recipient} ${s.city}`.toLowerCase().includes(query.toLowerCase());
    const matchR = filter === 'Tutti' || s.reason === filter;
    return matchQ && matchR;
  }), [stocks, query, filter]);
  const pending = stocks.filter(s => s.status === 'In giacenza').length;
  const emailsSent = stocks.filter(s => s.status === 'Email inviata').length;
  const inReDelivery = stocks.filter(s => s.status === 'In riconsegna').length;
  const sendEmail = (stock: Stock, body: string) => {
    const now = new Date();
    const ts = `${now.getDate()} ${['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'][now.getMonth()]} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newEvent: StockEvent = {
      id: `e-${Date.now()}`,
      kind: 'email_sent',
      timestamp: ts,
      title: `Email inviata a ${stock.carrier}`,
      description: `Richiesta di riconsegna inviata a ${CARRIER_EMAILS[stock.carrier]}.`,
      author: 'Magazzino Centrale'
    };
    setStocks(prev => prev.map(s => s.id === stock.id ? {
      ...s,
      status: 'Email inviata',
      emailSentAt: ts,
      events: [...s.events, newEvent]
    } : s));
    setEmailModal(null);
    setConfirmation(`Email inviata a ${CARRIER_EMAILS[stock.carrier]} per la giacenza ${stock.id}.`);
    setTimeout(() => setConfirmation(null), 4000);
    // Simulate carrier auto-reply after 3s
    setTimeout(() => {
      const replyTs = ts.replace(/\d\d:\d\d$/, () => {
        const d = new Date();
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes() + 1).padStart(2, '0')}`;
      });
      const reply: StockEvent = {
        id: `e-${Date.now() + 1}`,
        kind: 'email_reply',
        timestamp: replyTs,
        title: `Risposta da ${stock.carrier}`,
        description: `Ricevuta. Confermiamo la presa in carico della richiesta. Nuovo tentativo di consegna pianificato nei prossimi giorni lavorativi.`,
        author: `${stock.carrier} Support`
      };
      setStocks(prev => prev.map(s => s.id === stock.id ? {
        ...s,
        events: [...s.events, reply]
      } : s));
    }, 3000);
  };
  const selectedStock = selectedId ? stocks.find(s => s.id === selectedId) ?? null : null;
  if (selectedStock) {
    return <GiacenzaDetail stock={selectedStock} onBack={() => setSelectedId(null)} onSendEmail={() => setEmailModal(selectedStock)} emailModal={emailModal} onCloseEmailModal={() => setEmailModal(null)} onSubmitEmail={body => sendEmail(selectedStock, body)} confirmation={confirmation} data-id="element-1845" />;
  }
  return <div className="space-y-6" data-id="element-1846">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-1847">
        <div data-id="element-1848">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-1849">
            Giacenze
          </h1>
        </div>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1850">
        <KpiCard label="In giacenza" value={String(pending)} data-id="element-1851" />
        <KpiCard label="Email inviate" value={String(emailsSent)} data-id="element-1852" />
        <KpiCard label="In riconsegna" value={String(inReDelivery)} data-id="element-1853" />
        <KpiCard label="Totale giacenze" value={String(stocks.length)} data-id="element-1854" />
      </section>

      {confirmation && <div className="flex items-start gap-2 p-3 bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 rounded-lg" data-id="element-1855">
          <CheckCircle2 className="h-4 w-4 text-[#D4FF3A] shrink-0 mt-0.5" data-id="element-1856" />
          <p className="text-sm text-[#FAFAFA]" data-id="element-1857">{confirmation}</p>
        </div>}

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-1858">
        <div className="p-4 sm:p-5 flex items-center gap-3 flex-wrap border-b border-[#1F1F1F]" data-id="element-1859">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] overflow-x-auto" data-id="element-1860">
            {(['Tutti', 'Mancata firma', 'Negozio chiuso'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${filter === f ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-1861">
                  {f}
                </button>)}
          </div>
          <div className="relative ml-auto w-full sm:w-auto" data-id="element-1862">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1863" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca per ID, destinatario, città..." className="w-full sm:w-72 pl-9 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1864" />
          </div>
        </div>

        {filtered.length === 0 ? <div className="p-12 text-center" data-id="element-1865">
            <PackageX className="h-8 w-8 text-[#6B6B6B] mx-auto mb-3" data-id="element-1866" />
            <p className="text-sm text-[#9A9A9A]" data-id="element-1867">Nessuna giacenza trovata.</p>
          </div> : <div className="overflow-x-auto" data-id="element-1868">
            <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-1869">
              <thead data-id="element-1870">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-1871">
                  <th className="px-5 py-3 font-medium" data-id="element-1872">ID</th>
                  <th className="px-5 py-3 font-medium" data-id="element-1873">Spedizione</th>
                  <th className="px-5 py-3 font-medium" data-id="element-1874">Corriere</th>
                  <th className="px-5 py-3 font-medium" data-id="element-1875">Destinatario</th>
                  <th className="px-5 py-3 font-medium" data-id="element-1876">Motivo</th>
                  <th className="px-5 py-3 font-medium" data-id="element-1877">In giacenza dal</th>
                  <th className="px-5 py-3 font-medium" data-id="element-1878">Stato</th>
                  <th className="px-5 py-3 font-medium text-right" data-id="element-1879">Azioni</th>
                </tr>
              </thead>
              <tbody data-id="element-1880">
                {filtered.map(s => {
              const reasonCfg = REASON_CONFIG[s.reason];
              const ReasonIcon = reasonCfg.icon;
              const statusCfg = STATUS_CONFIG[s.status];
              const StatusIcon = statusCfg.icon;
              const canSendEmail = s.status === 'In giacenza';
              return <tr key={s.id} onClick={() => setSelectedId(s.id)} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors cursor-pointer" data-id="element-1881">
                      <td className="px-5 py-3.5 font-mono text-xs text-[#9A9A9A]" data-id="element-1882">
                        {s.id}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-[#FAFAFA]" data-id="element-1883">
                        {s.shipmentRef}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-1884">
                        <div className="flex items-center gap-2.5" data-id="element-1885">
                          <div className="h-7 w-9 bg-white rounded p-1 flex items-center justify-center shrink-0" data-id="element-1886">
                            <img src={CARRIER_LOGOS[s.carrier]} alt={s.carrier} className="max-w-full max-h-full object-contain" data-id="element-1887" />
                          </div>
                          <span className="text-sm text-[#FAFAFA]" data-id="element-1888">
                            {s.carrier}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5" data-id="element-1889">
                        <div className="text-sm text-[#FAFAFA]" data-id="element-1890">
                          {s.recipient}
                        </div>
                        <div className="text-[11px] text-[#6B6B6B]" data-id="element-1891">
                          {s.address}, {s.city}
                        </div>
                      </td>
                      <td className="px-5 py-3.5" data-id="element-1892">
                        <div className="flex items-center gap-1.5" data-id="element-1893">
                          <ReasonIcon className={`h-3.5 w-3.5 ${reasonCfg.color}`} data-id="element-1894" />
                          <span className="text-xs text-[#FAFAFA]" data-id="element-1895">
                            {s.reason}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5" data-id="element-1896">
                        <div className="text-xs font-mono text-[#9A9A9A]" data-id="element-1897">
                          {s.sinceDate}
                        </div>
                        <div className="text-[10px] text-[#6B6B6B]" data-id="element-1898">
                          da {s.daysInStock} giorni
                        </div>
                      </td>
                      <td className="px-5 py-3.5" data-id="element-1899">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`} data-id="element-1900">
                          <StatusIcon className="h-3 w-3" data-id="element-1901" />
                          {s.status}
                        </span>
                        {s.emailSentAt && <div className="text-[10px] text-[#6B6B6B] mt-0.5" data-id="element-1902">
                            {s.emailSentAt}
                          </div>}
                      </td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()} data-id="element-1903">
                        <div className="flex items-center justify-end gap-2" data-id="element-1904">
                          <button type="button" onClick={() => setEmailModal(s)} disabled={!canSendEmail} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${canSendEmail ? 'bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28]' : 'bg-[#1F1F1F] border border-[#262626] text-[#6B6B6B] cursor-not-allowed'}`} data-id="element-1905">
                            <Mail className="h-3.5 w-3.5" data-id="element-1906" />
                            {canSendEmail ? 'Invia email al corriere' : 'Email inviata'}
                          </button>
                          <button type="button" onClick={() => setSelectedId(s.id)} className="p-1.5 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#1F1F1F] transition" aria-label="Vedi dettaglio" data-id="element-1907">
                            <ChevronRight className="h-4 w-4" data-id="element-1908" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {emailModal && <EmailModal stock={emailModal} onCancel={() => setEmailModal(null)} onSend={body => sendEmail(emailModal, body)} data-id="element-1909" />}
    </div>;
}
function EmailModal({
  stock,
  onCancel,
  onSend
}: {
  stock: Stock;
  onCancel: () => void;
  onSend: (body: string) => void;
}) {
  const defaultBody = `Buongiorno,

vi segnaliamo che la spedizione ${stock.shipmentRef} risulta in giacenza presso ${stock.hubLocation} dal ${stock.sinceDate} per il seguente motivo:

${stock.reason === 'Mancata firma' ? 'Mancata firma del destinatario al momento della consegna.' : 'Punto vendita chiuso al momento del passaggio del corriere.'}

Destinatario: ${stock.recipient}
Indirizzo: ${stock.address}, ${stock.city}

Vi chiediamo gentilmente di organizzare una riconsegna nel più breve tempo possibile.

Cordiali saluti,
Servizio Logistica`;
  const [body, setBody] = useState(defaultBody);
  const subject = `Riconsegna giacenza ${stock.shipmentRef} — ${stock.recipient}`;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1910">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} data-id="element-1911" />
      <div className="relative w-full max-w-2xl bg-[#0A0A0A] border border-[#262626] rounded-xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1912">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#262626]" data-id="element-1913">
          <div className="flex items-center gap-3 min-w-0" data-id="element-1914">
            <div className="h-10 w-10 rounded-lg bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 flex items-center justify-center shrink-0" data-id="element-1915">
              <Mail className="h-4 w-4 text-[#D4FF3A]" data-id="element-1916" />
            </div>
            <div className="min-w-0" data-id="element-1917">
              <h3 className="text-base font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-1918">
                Email al corriere
              </h3>
              <p className="text-xs text-[#9A9A9A] mt-0.5 truncate" data-id="element-1919">
                {stock.carrier} · {CARRIER_EMAILS[stock.carrier]}
              </p>
            </div>
          </div>
          <button type="button" onClick={onCancel} className="p-1 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition shrink-0" data-id="element-1920">
            <X className="h-4 w-4" data-id="element-1921" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4" data-id="element-1922">
          <div className="bg-[#181818] border border-[#262626] rounded-lg p-3 space-y-2" data-id="element-1923">
            <Row label="A" value={CARRIER_EMAILS[stock.carrier]} mono data-id="element-1924" />
            <Row label="Oggetto" value={subject} data-id="element-1925" />
            <Row label="Riferimento" value={stock.id} mono data-id="element-1926" />
          </div>

          <div data-id="element-1927">
            <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-1928">
              Corpo email
            </label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={12} className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40 resize-none font-mono leading-relaxed" data-id="element-1929" />
          </div>

          <p className="text-xs text-[#6B6B6B] flex items-start gap-1.5" data-id="element-1930">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" data-id="element-1931" />
            L'email verrà inviata automaticamente al referente giacenze del
            corriere e lo stato della giacenza passerà a "Email inviata".
          </p>
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2" data-id="element-1932">
          <button type="button" onClick={onCancel} className="px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-1933">
            Annulla
          </button>
          <button type="button" onClick={() => onSend(body)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1934">
            <Send className="h-4 w-4" data-id="element-1935" />
            Invia email
          </button>
        </div>
      </div>
    </div>;
}
function Row({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div className="flex items-baseline gap-3 text-xs" data-id="element-1936">
      <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium w-20 shrink-0" data-id="element-1937">
        {label}
      </span>
      <span className={`text-[#FAFAFA] ${mono ? 'font-mono' : ''}`} data-id="element-1938">
        {value}
      </span>
    </div>;
}
function GiacenzaDetail({
  stock,
  onBack,
  onSendEmail,
  emailModal,
  onCloseEmailModal,
  onSubmitEmail,
  confirmation
}: {
  stock: Stock;
  onBack: () => void;
  onSendEmail: () => void;
  emailModal: Stock | null;
  onCloseEmailModal: () => void;
  onSubmitEmail: (body: string) => void;
  confirmation: string | null;
}) {
  const reasonCfg = REASON_CONFIG[stock.reason];
  const ReasonIcon = reasonCfg.icon;
  const statusCfg = STATUS_CONFIG[stock.status];
  const StatusIcon = statusCfg.icon;
  const canSendEmail = stock.status === 'In giacenza';
  const sortedEvents = [...stock.events].reverse();
  return <div className="space-y-6" data-id="element-1939">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-1940">
        <ArrowLeft className="h-4 w-4" data-id="element-1941" />
        <span data-id="element-1942">Torna a Giacenze</span>
      </button>

      {/* Header */}
      <div className="bg-[#181818] border border-[#262626] rounded-xl p-4 sm:p-5" data-id="element-1943">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-1944">
          <div className="flex items-start gap-4" data-id="element-1945">
            <div className="h-12 w-12 rounded-lg bg-[#0A0A0A] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-1946">
              <PackageX className="h-5 w-5 text-[#F5A524]" data-id="element-1947" />
            </div>
            <div data-id="element-1948">
              <div className="flex items-center gap-2 mb-1" data-id="element-1949">
                <Package className="h-3.5 w-3.5 text-[#6B6B6B]" data-id="element-1950" />
                <span className="text-xs font-mono text-[#9A9A9A]" data-id="element-1951">
                  Spedizione {stock.shipmentRef}
                </span>
              </div>
              <div className="text-xl font-semibold text-[#FAFAFA] font-heading font-mono" data-id="element-1952">
                {stock.id}
              </div>
              <div className="text-sm text-[#9A9A9A] mt-0.5" data-id="element-1953">
                {stock.recipient} — {stock.city}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap" data-id="element-1954">
            <div data-id="element-1955">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1956">
                Stato
              </div>
              <div className="mt-1.5" data-id="element-1957">
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`} data-id="element-1958">
                  <StatusIcon className="h-3 w-3" data-id="element-1959" />
                  {stock.status}
                </span>
              </div>
            </div>
            <div data-id="element-1960">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1961">
                In giacenza da
              </div>
              <div className="text-sm font-semibold text-[#FAFAFA] font-mono mt-1" data-id="element-1962">
                {stock.daysInStock} giorni
              </div>
            </div>
            <div data-id="element-1963">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1964">
                Motivo
              </div>
              <div className="mt-1 flex items-center gap-1.5" data-id="element-1965">
                <ReasonIcon className={`h-3.5 w-3.5 ${reasonCfg.color}`} data-id="element-1966" />
                <span className="text-sm text-[#FAFAFA]" data-id="element-1967">{stock.reason}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="mt-5 pt-5 border-t border-[#262626] flex flex-wrap items-center gap-2" data-id="element-1968">
          <button type="button" onClick={onSendEmail} disabled={!canSendEmail} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition ${canSendEmail ? 'bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28]' : 'bg-[#1F1F1F] border border-[#262626] text-[#6B6B6B] cursor-not-allowed'}`} data-id="element-1969">
            <Mail className="h-4 w-4" data-id="element-1970" />
            {canSendEmail ? 'Invia email al corriere' : 'Email già inviata'}
          </button>
          {!canSendEmail && <span className="text-xs text-[#9A9A9A]" data-id="element-1971">
              Aspettando aggiornamento del corriere — l'invio sarà di nuovo
              disponibile se la giacenza torna in stato attivo.
            </span>}
        </div>
      </div>

      {confirmation && <div className="flex items-start gap-2 p-3 bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 rounded-lg" data-id="element-1972">
          <CheckCircle2 className="h-4 w-4 text-[#D4FF3A] shrink-0 mt-0.5" data-id="element-1973" />
          <p className="text-sm text-[#FAFAFA]" data-id="element-1974">{confirmation}</p>
        </div>}

      {/* Details + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-1975">
        {/* Details column */}
        <div className="lg:col-span-2 space-y-4" data-id="element-1976">
          <div className="bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-1977">
            <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading mb-4" data-id="element-1978">
              Dettagli giacenza
            </h3>
            <div className="space-y-3" data-id="element-1979">
              <DetailRow icon={<Inbox className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-1981" />} label="ID giacenza" value={stock.id} mono data-id="element-1980" />
              <DetailRow icon={<Package className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-1983" />} label="Spedizione" value={stock.shipmentRef} mono data-id="element-1982" />
              <DetailRow icon={<Calendar className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-1985" />} label="In giacenza dal" value={stock.sinceDate} data-id="element-1984" />
              <DetailRow icon={<ReasonIcon className={`h-3.5 w-3.5 ${reasonCfg.color}`} data-id="element-1987" />} label="Motivo" value={stock.reason} data-id="element-1986" />
            </div>
          </div>

          <div className="bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-1988">
            <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading mb-4" data-id="element-1989">
              Corriere & destinazione
            </h3>
            <div className="space-y-3" data-id="element-1990">
              <div className="flex items-center gap-3" data-id="element-1991">
                <div className="h-10 w-12 bg-white rounded p-1 flex items-center justify-center shrink-0" data-id="element-1992">
                  <img src={CARRIER_LOGOS[stock.carrier]} alt={stock.carrier} className="max-w-full max-h-full object-contain" data-id="element-1993" />
                </div>
                <div className="min-w-0" data-id="element-1994">
                  <div className="text-sm text-[#FAFAFA]" data-id="element-1995">{stock.carrier}</div>
                  <div className="text-xs font-mono text-[#9A9A9A] truncate" data-id="element-1996">
                    {CARRIER_EMAILS[stock.carrier]}
                  </div>
                </div>
              </div>
              <div className="border-t border-[#262626] pt-3 space-y-3" data-id="element-1997">
                <DetailRow icon={<Truck className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-1999" />} label="Hub di giacenza" value={stock.hubLocation} data-id="element-1998" />
                <DetailRow icon={<MapPin className="h-3.5 w-3.5 text-[#9A9A9A]" data-id="element-2001" />} label="Destinatario" value={stock.recipient} secondary={`${stock.address}, ${stock.city}`} data-id="element-2000" />
              </div>
            </div>
          </div>
        </div>

        {/* Timeline column */}
        <div className="lg:col-span-3 bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-2002">
          <div className="flex items-center justify-between mb-4" data-id="element-2003">
            <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading" data-id="element-2004">
              Storico azioni
            </h3>
            <span className="text-[11px] text-[#6B6B6B]" data-id="element-2005">
              {stock.events.length} eventi
            </span>
          </div>

          <ol className="relative" data-id="element-2006">
            <span aria-hidden className="absolute left-3.5 top-2 bottom-2 w-px bg-[#262626]" data-id="element-2007" />
            {sortedEvents.map((event, idx) => <TimelineItem key={event.id} event={event} isLatest={idx === 0} data-id="element-2008" />)}
          </ol>
        </div>
      </div>

      {emailModal && <EmailModal stock={emailModal} onCancel={onCloseEmailModal} onSend={onSubmitEmail} data-id="element-2009" />}
    </div>;
}
function DetailRow({
  icon,
  label,
  value,
  secondary,
  mono
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  secondary?: string;
  mono?: boolean;
}) {
  return <div className="flex items-start gap-3" data-id="element-2010">
      <div className="h-7 w-7 rounded-lg bg-[#0A0A0A] border border-[#262626] flex items-center justify-center shrink-0 mt-0.5" data-id="element-2011">
        {icon}
      </div>
      <div className="min-w-0" data-id="element-2012">
        <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-2013">
          {label}
        </div>
        <div className={`text-sm text-[#FAFAFA] mt-0.5 ${mono ? 'font-mono' : ''}`} data-id="element-2014">
          {value}
        </div>
        {secondary && <div className="text-[11px] text-[#6B6B6B] mt-0.5" data-id="element-2015">{secondary}</div>}
      </div>
    </div>;
}
const EVENT_CONFIG: Record<EventKind, {
  icon: ComponentType<any>;
  bg: string;
  ring: string;
  text: string;
}> = {
  created: {
    icon: PackageX,
    bg: 'bg-[#F5A524]/10',
    ring: 'ring-[#F5A524]/30',
    text: 'text-[#F5A524]'
  },
  email_sent: {
    icon: Send,
    bg: 'bg-[#D4FF3A]/10',
    ring: 'ring-[#D4FF3A]/30',
    text: 'text-[#D4FF3A]'
  },
  email_reply: {
    icon: Mail,
    bg: 'bg-[#5DA9FF]/10',
    ring: 'ring-[#5DA9FF]/30',
    text: 'text-[#5DA9FF]'
  },
  redelivery: {
    icon: Truck,
    bg: 'bg-[#D4FF3A]/10',
    ring: 'ring-[#D4FF3A]/30',
    text: 'text-[#D4FF3A]'
  },
  resolved: {
    icon: CheckCircle2,
    bg: 'bg-[#D4FF3A]/10',
    ring: 'ring-[#D4FF3A]/30',
    text: 'text-[#D4FF3A]'
  },
  note: {
    icon: AlertCircle,
    bg: 'bg-[#1F1F1F]',
    ring: 'ring-[#262626]',
    text: 'text-[#9A9A9A]'
  }
};
function TimelineItem({
  event,
  isLatest
}: {
  event: StockEvent;
  isLatest: boolean;
}) {
  const cfg = EVENT_CONFIG[event.kind];
  const Icon = cfg.icon;
  const isReply = event.kind === 'email_reply';
  return <li className="relative pl-10 pb-5 last:pb-0" data-id="element-2016">
      <span className={`absolute left-0 top-0 h-7 w-7 rounded-full flex items-center justify-center ring-2 ring-offset-2 ring-offset-[#181818] ${cfg.bg} ${cfg.ring}`} data-id="element-2017">
        <Icon className={`h-3.5 w-3.5 ${cfg.text}`} data-id="element-2018" />
      </span>
      <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2019">
        <span className="text-sm font-medium text-[#FAFAFA]" data-id="element-2020">
          {event.title}
        </span>
        {isLatest && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-[#D4FF3A]/10 text-[#D4FF3A] border border-[#D4FF3A]/30" data-id="element-2021">
            Ultimo
          </span>}
      </div>
      <div className="text-[11px] font-mono text-[#6B6B6B] mb-1.5" data-id="element-2022">
        {event.timestamp}
        {event.author && <span className="text-[#9A9A9A]" data-id="element-2023"> · {event.author}</span>}
      </div>
      {event.description && <div className={`text-xs leading-relaxed rounded-lg px-3 py-2 ${isReply ? 'bg-[#5DA9FF]/5 border border-[#5DA9FF]/20 text-[#FAFAFA]' : 'bg-[#0A0A0A] border border-[#262626] text-[#9A9A9A]'}`} data-id="element-2024">
          {event.description}
        </div>}
    </li>;
}